// HKDSE 2019 Paper 1 Knowledge Graph Samples - Complete Set
// Organized by question types with systematic naming

export const hkdse2019Samples_complete = {
  algebra: [
    {
      id: 'hkdse2019_algebra_01',
      problem: "Given that $h$ is the subject of the formula $9(h + 6k) = 7h + 8$.",
      problemType: "algebra_formula",
      difficulty: "fundamental",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "linear_equation",
            name: "Linear Equation",
            type: "concepts",
            description: "Equation with variable h and parameter k",
            value: "9(h + 6k) = 7h + 8",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "subject_formula",
            name: "Subject of Formula",
            type: "concepts",
            description: "Making h the subject of the formula",
            value: "h = ...",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "variable_h",
            name: "Variable h",
            type: "variables",
            description: "Variable to be made the subject",
            value: "h",
            importance: 8,
            x: -200, y: 0, z: 0
          },
          {
            id: "parameter_k",
            name: "Parameter k",
            type: "variables",
            description: "Parameter in the equation",
            value: "k",
            importance: 7,
            x: 200, y: 0, z: 0
          }
        ],
        links: [
          {
            from: "linear_equation",
            to: "variable_h",
            type: "contains",
            strength: 9
          },
          {
            from: "linear_equation",
            to: "parameter_k",
            type: "contains",
            strength: 8
          },
          {
            from: "subject_formula",
            to: "linear_equation",
            type: "transforms",
            strength: 9
          }
        ]
      }
    },
    {
      id: 'hkdse2019_algebra_02',
      problem: "Simplify $\\frac{3}{7x - 6} - \\frac{2}{5x - 4}$.",
      problemType: "algebra_fractions",
      difficulty: "intermediate",
      description: "Simplifying algebraic fractions with different denominators",
      graphData: {
        nodes: [
          {
            id: "algebraic_fractions",
            name: "Algebraic Fractions",
            type: "concepts",
            description: "Fractions with algebraic expressions",
            value: "3/(7x-6) - 2/(5x-4)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_denominator",
            name: "Common Denominator",
            type: "methods",
            description: "Finding common denominator for subtraction",
            value: "(7x-6)(5x-4)",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "numerator_addition",
            name: "Numerator Addition",
            type: "methods",
            description: "Adding numerators after finding common denominator",
            value: "3(5x-4) - 2(7x-6)",
            importance: 8,
            x: 200, y: 0, z: 0
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "methods",
            description: "Simplifying the final expression",
            value: "Final simplified form",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "algebraic_fractions",
            to: "common_denominator",
            type: "requires",
            strength: 9
          },
          {
            from: "common_denominator",
            to: "numerator_addition",
            type: "enables",
            strength: 9
          },
          {
            from: "numerator_addition",
            to: "simplification",
            type: "leads_to",
            strength: 8
          }
        ]
      }
    }
  ],
  geometry: [
    {
      id: 'hkdse2019_geometry_01',
      problem: "The length and width of a rectangle are $24$ cm and $(13 + r)$ cm respectively. If the length of the diagonal of the rectangle is $(17 - 3r)$ cm, find $r$.",
      problemType: "geometry_pythagoras",
      difficulty: "intermediate",
      description: "Using Pythagoras theorem to find unknown variable",
      graphData: {
        nodes: [
          {
            id: "rectangle_dimensions",
            name: "Rectangle Dimensions",
            type: "concepts",
            description: "Length and width of rectangle",
            value: "Length = 24cm, Width = (13+r)cm",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "diagonal_length",
            name: "Diagonal Length",
            type: "concepts",
            description: "Length of diagonal in terms of r",
            value: "(17-3r) cm",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "pythagoras_theorem",
            name: "Pythagoras Theorem",
            type: "formulas",
            description: "a² + b² = c² for right triangle",
            value: "24² + (13+r)² = (17-3r)²",
            importance: 10,
            x: 0, y: 0, z: 0
          },
          {
            id: "variable_r",
            name: "Variable r",
            type: "variables",
            description: "Unknown variable to be found",
            value: "r",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "rectangle_dimensions",
            to: "pythagoras_theorem",
            type: "provides_data",
            strength: 9
          },
          {
            from: "diagonal_length",
            to: "pythagoras_theorem",
            type: "provides_data",
            strength: 9
          },
          {
            from: "pythagoras_theorem",
            to: "variable_r",
            type: "solves_for",
            strength: 10
          }
        ]
      }
    }
  ],
  factorization: [
    {
      id: 'hkdse2019_factorization_01',
      problem: "Factorize: (a) $4m^2 - 9$ (b) $2m^2 n + 7mn - 15n$ (c) $4m^2 - 9 - 2m^2 n - 7mn + 15n$",
      problemType: "factorization_polynomials",
      difficulty: "intermediate",
      description: "Factorizing different types of polynomials",
      graphData: {
        nodes: [
          {
            id: "difference_squares",
            name: "Difference of Squares",
            type: "concepts",
            description: "a² - b² = (a+b)(a-b)",
            value: "4m² - 9 = (2m+3)(2m-3)",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "quadratic_trinomial",
            name: "Quadratic Trinomial",
            type: "concepts",
            description: "Factoring quadratic expressions",
            value: "2m²n + 7mn - 15n = n(2m² + 7m - 15)",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "combining_terms",
            name: "Combining Terms",
            type: "methods",
            description: "Combining factored expressions",
            value: "Using results from (a) and (b)",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "common_factors",
            name: "Common Factors",
            type: "methods",
            description: "Finding common factors in expressions",
            value: "n is common factor in (b)",
            importance: 7,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "difference_squares",
            to: "combining_terms",
            type: "used_in",
            strength: 8
          },
          {
            from: "quadratic_trinomial",
            to: "combining_terms",
            type: "used_in",
            strength: 8
          },
          {
            from: "common_factors",
            to: "quadratic_trinomial",
            type: "applied_to",
            strength: 8
          }
        ]
      }
    }
  ],
  percentages: [
    {
      id: 'hkdse2019_percentages_01',
      problem: "A wallet is sold at a $25\\%$ discount off its marked price. The selling price of the wallet is $\\$690$. (a) Find the marked price of the wallet. (b) After selling the wallet, the profit percentage is $15\\%$. Find the cost of the wallet.",
      problemType: "percentages_profit_loss",
      difficulty: "intermediate",
      description: "Solving problems involving discounts and profit percentages",
      graphData: {
        nodes: [
          {
            id: "discount_calculation",
            name: "Discount Calculation",
            type: "concepts",
            description: "25% discount from marked price",
            value: "Selling Price = Marked Price × 0.75",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "marked_price",
            name: "Marked Price",
            type: "variables",
            description: "Original price before discount",
            value: "MP = $690 ÷ 0.75",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "profit_percentage",
            name: "Profit Percentage",
            type: "concepts",
            description: "15% profit on cost price",
            value: "Profit = 15% of Cost Price",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "cost_price",
            name: "Cost Price",
            type: "variables",
            description: "Cost price to be found",
            value: "CP = SP ÷ 1.15",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "selling_price",
            name: "Selling Price",
            type: "variables",
            description: "Given selling price",
            value: "$690",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "discount_calculation",
            to: "marked_price",
            type: "calculates",
            strength: 9
          },
          {
            from: "selling_price",
            to: "discount_calculation",
            type: "used_in",
            strength: 9
          },
          {
            from: "profit_percentage",
            to: "cost_price",
            type: "calculates",
            strength: 8
          },
          {
            from: "selling_price",
            to: "cost_price",
            type: "used_in",
            strength: 8
          }
        ]
      }
    }
  ],
  inequalities: [
    {
      id: 'hkdse2019_inequalities_01',
      problem: "(a) Solve the inequality $\\frac{7x + 26}{4} \\leq 2(3x - 1)$. (b) Find the number of integers that satisfy both the inequality $\\frac{7x + 26}{4} \\leq 2(3x - 1)$ and the inequality $45 - 5x \\geq 0$.",
      problemType: "inequalities_linear",
      difficulty: "intermediate",
      description: "Solving linear inequalities and finding integer solutions",
      graphData: {
        nodes: [
          {
            id: "linear_inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "Inequality with fractions and variables",
            value: "(7x+26)/4 ≤ 2(3x-1)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "fraction_elimination",
            name: "Fraction Elimination",
            type: "methods",
            description: "Multiplying both sides to eliminate fractions",
            value: "Multiply by 4 to eliminate denominator",
            importance: 8,
            x: -200, y: 0, z: 0
          },
          {
            id: "second_inequality",
            name: "Second Inequality",
            type: "concepts",
            description: "Additional inequality constraint",
            value: "45 - 5x ≥ 0",
            importance: 8,
            x: 200, y: 0, z: 0
          },
          {
            id: "integer_solutions",
            name: "Integer Solutions",
            type: "concepts",
            description: "Finding integer values satisfying both inequalities",
            value: "Count integers in solution set",
            importance: 7,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "linear_inequality",
            to: "fraction_elimination",
            type: "solved_by",
            strength: 9
          },
          {
            from: "second_inequality",
            to: "integer_solutions",
            type: "constrains",
            strength: 8
          },
          {
            from: "linear_inequality",
            to: "integer_solutions",
            type: "constrains",
            strength: 8
          }
        ]
      }
    }
  ],
  word_problems: [
    {
      id: 'hkdse2019_word_problems_01',
      problem: "At an amusement park, the ratio of the number of adults to the number of children is $13:6$. If $9$ adults and $24$ children enter the amusement park, the ratio of the number of adults to the number of children becomes $8:7$. Find the original number of adults at the amusement park.",
      problemType: "word_problems_ratio",
      difficulty: "intermediate",
      description: "Solving ratio problems with changing quantities",
      graphData: {
        nodes: [
          {
            id: "original_ratio",
            name: "Original Ratio",
            type: "concepts",
            description: "Initial ratio of adults to children",
            value: "Adults:Children = 13:6",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "new_ratio",
            name: "New Ratio",
            type: "concepts",
            description: "Ratio after adding people",
            value: "Adults:Children = 8:7",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "adults_added",
            name: "Adults Added",
            type: "variables",
            description: "Number of adults entering",
            value: "9 adults",
            importance: 7,
            x: -200, y: 0, z: 0
          },
          {
            id: "children_added",
            name: "Children Added",
            type: "variables",
            description: "Number of children entering",
            value: "24 children",
            importance: 7,
            x: 200, y: 0, z: 0
          },
          {
            id: "original_adults",
            name: "Original Adults",
            type: "variables",
            description: "Original number of adults to find",
            value: "x adults",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "original_ratio",
            to: "original_adults",
            type: "involves",
            strength: 9
          },
          {
            from: "new_ratio",
            to: "original_adults",
            type: "involves",
            strength: 9
          },
          {
            from: "adults_added",
            to: "new_ratio",
            type: "affects",
            strength: 8
          },
          {
            from: "children_added",
            to: "new_ratio",
            type: "affects",
            strength: 8
          }
        ]
      }
    }
  ]
}; 